import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { BRANCHES, LOGO } from "../../lib/constants";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Branches", href: "#branches" },
  { label: "Facilities", href: "#facilities" },
  { label: "Fees", href: "#fees" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToBranch = (id) => {
    const el = document.getElementById(`branch-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0F0F0F]/85 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a
            href="#home"
            data-testid="nav-logo"
            className="flex items-center gap-2.5 group"
          >
            <div className="w-12 h-12 rounded-md bg-white grid place-items-center overflow-hidden ring-1 ring-white/10">
              <img
                src={LOGO}
                alt="Justudy Library logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-base tracking-tight text-white">
                Justudy Library
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#FACC15]">
                & Co-working Space
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="text-sm tracking-wide text-white/80 hover:text-[#FACC15] transition-colors font-medium"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="branch-selector"
                className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/90 hover:text-[#FACC15] px-3 py-2 border border-white/20 hover:border-[#FACC15] rounded-sm transition-colors font-semibold"
              >
                Select Branch <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#1A1A1A] border-white/10 text-white min-w-[220px]"
              >
                {Object.values(BRANCHES).map((b) => (
                  <DropdownMenuItem
                    key={b.id}
                    data-testid={`branch-option-${b.id}`}
                    onClick={() => scrollToBranch(b.id)}
                    className="cursor-pointer focus:bg-[#FACC15]/10 focus:text-[#FACC15]"
                  >
                    <div>
                      <div className="font-medium">{b.name}</div>
                      <div className="text-xs text-white/50">{b.timingShort}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              href="tel:+918447496808"
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0F0F0F] font-semibold px-4 py-2 text-sm rounded-sm transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" />
              Call
            </a>

            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0F0F0F] border-t border-white/10"
        >
          <div className="px-6 py-6 space-y-4">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="block text-white/90 hover:text-[#FACC15] py-2 text-base font-medium"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-3 border-t border-white/10 space-y-2">
              <div className="text-xs uppercase tracking-[0.2em] text-[#FACC15] font-semibold">
                Branches
              </div>
              {Object.values(BRANCHES).map((b) => (
                <button
                  key={b.id}
                  onClick={() => scrollToBranch(b.id)}
                  data-testid={`mobile-branch-${b.id}`}
                  className="block w-full text-left text-white/90 hover:text-[#FACC15] py-1.5"
                >
                  {b.name}{" "}
                  <span className="text-xs text-white/40">· {b.timingShort}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
